import {
  GraphComponent,
  HtmlCanvasVisual,
  IEdge,
  ILabel,
  INode,
  IRenderContext,
  IVisualCreator,
  SvgExport,
} from '@ardoq/yfiles';
import {
  CANVAS_MAXIMUM_AREA,
  CANVAS_MAXIMUM_DIMENSION,
  EXPORT_MARGIN_PX,
} from './consts';
import { renderUntilImagesSettle } from './recordedSvgDocument';

/**
 * the scale at which to render the graph directly to a canvas.
 *
 * increased up to the devicePixelRatio for crisp rendering, and limited so the rendered canvas stays within {@link CANVAS_MAXIMUM_DIMENSION} and {@link CANVAS_MAXIMUM_AREA}. unlike the SVG export path, the limits are applied to the real destination canvas, so they can be honoured at full resolution without the browser's stricter SVG image-decode limit.
 *
 * `marginPx` is the whitespace the caller adds around the scaled content, on each
 * side, and so counts against the dimension limit.
 */
export const directExportScale = (
  contentBounds: { width: number; height: number },
  devicePixelRatio: number,
  marginPx: number
): number => {
  const { width, height } = contentBounds;
  const area = width * height;
  const maxDimension = Math.max(width, height);

  return Math.min(
    devicePixelRatio,
    (CANVAS_MAXIMUM_DIMENSION - 2 * marginPx) / maxDimension,
    Math.sqrt(CANVAS_MAXIMUM_AREA / area)
  );
};

const nodeDepth = (
  getParent: (node: INode) => INode | null,
  node: INode
): number => {
  let depth = 0;
  let parent = getParent(node);
  while (parent) {
    depth += 1;
    parent = getParent(parent);
  }
  return depth;
};

/**
 * an item to render, in back-to-front paint order.
 *
 * `getCreator` fetches the yFiles visual creator fresh on each call: these are flyweights that must not be cached, since the same renderer instance is reconfigured per item on each `getVisualCreator` call. caching them aliases every item of a style to the last-fetched one.
 */
export type RenderItem = { getCreator: () => IVisualCreator };

export const collectRenderItems = (
  graph: GraphComponent['graph']
): RenderItem[] => {
  const nodes = graph.nodes.toArray();
  const getParent = (node: INode) => graph.getParent(node);
  const groupNodes = nodes
    .filter(node => graph.isGroupNode(node))
    .sort((a, b) => nodeDepth(getParent, a) - nodeDepth(getParent, b));
  const leafNodes = nodes.filter(node => !graph.isGroupNode(node));

  const nodeItem = (node: INode): RenderItem => ({
    getCreator: () => node.style.renderer.getVisualCreator(node, node.style),
  });
  const edgeItem = (edge: IEdge): RenderItem => ({
    getCreator: () => edge.style.renderer.getVisualCreator(edge, edge.style),
  });
  const labelItem = (label: ILabel): RenderItem => ({
    getCreator: () => label.style.renderer.getVisualCreator(label, label.style),
  });

  return [
    ...groupNodes.map(nodeItem),
    ...graph.edges.toArray().map(edgeItem),
    ...leafNodes.map(nodeItem),
    ...graph.labels.toArray().map(labelItem),
  ];
};

/**
 * @returns false if the item's visual is not an {@link HtmlCanvasVisual}, signalling that this graph is not fully canvas-rendered and the caller should fall back to the SVG export path.
 */
const renderItem = (
  item: RenderItem,
  renderContext: IRenderContext,
  ctx: CanvasRenderingContext2D
): boolean => {
  const visual = item.getCreator().createVisual(renderContext);
  if (visual === null) {
    return true;
  }
  if (!(visual instanceof HtmlCanvasVisual)) {
    return false;
  }
  ctx.save();
  visual.render(renderContext, ctx);
  ctx.restore();
  return true;
};

/**
 * renders a fully canvas-rendered ({@link HtmlCanvasVisual}) graph directly to a 2D canvas at the given scale, bypassing {@link SvgExport}.
 *
 * this avoids serialising the graph to an SVG stuffed with base64 rasters, which the browser fails to decode for large diagrams (producing a corrupt export).
 *
 * node icons, shapes and component images load asynchronously and are only resolved by the live (virtualised) view for on-screen nodes, so the graph is rendered repeatedly, waiting for the images drawn each pass to decode, until they settle. this mirrors the live view's draw-then-redraw-on-load cycle.
 *
 * @returns the rendered canvas, or null if the graph contains a non-canvas visual (in which case the caller should fall back to the SVG export path).
 */
export const renderGraphComponentToCanvas = async (
  graphComponent: GraphComponent,
  scale: number
): Promise<HTMLCanvasElement | null> => {
  const { graph } = graphComponent;
  const world = graphComponent.contentBounds;

  const canvas = document.createElement('canvas');
  // Floor the scaled content: `directExportScale` bounds `content * scale + 2 *
  // margin` by the canvas limit in exact arithmetic, and rounding up can land a
  // pixel past it, which the browser refuses outright. The margin absorbs the
  // sub-pixel.
  canvas.width = Math.floor(world.width * scale) + 2 * EXPORT_MARGIN_PX;
  canvas.height = Math.floor(world.height * scale) + 2 * EXPORT_MARGIN_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  const exporter = new SvgExport(world, scale);
  // full-detail render (the output scale is applied via the canvas transform below, not the level-of-detail zoom)
  exporter.zoom = 1;
  const renderContext = exporter.createRenderContext(graphComponent);

  const items = collectRenderItems(graph);

  const drawnImages = new Set<HTMLImageElement>();
  const originalDrawImage = ctx.drawImage.bind(ctx);
  ctx.drawImage = ((image: CanvasImageSource, ...args: number[]) => {
    if (image instanceof HTMLImageElement) {
      drawnImages.add(image);
    }
    return (
      originalDrawImage as (image: CanvasImageSource, ...args: number[]) => void
    )(image, ...args);
  }) as CanvasRenderingContext2D['drawImage'];

  const renderAllItems = (): boolean => {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(
      scale,
      0,
      0,
      scale,
      EXPORT_MARGIN_PX - world.x * scale,
      EXPORT_MARGIN_PX - world.y * scale
    );
    return items.every(item => renderItem(item, renderContext, ctx));
  };

  await document.fonts.ready;

  const outcome = await renderUntilImagesSettle(() =>
    renderAllItems() ? { images: drawnImages, isComplete: true } : null
  );

  ctx.drawImage = originalDrawImage;
  return outcome === 'aborted' ? null : canvas;
};
